import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const StreakContext = createContext(null);

export const useStreak = () => {
  const context = useContext(StreakContext);
  if (!context) {
    throw new Error('useStreak must be used within a StreakProvider');
  }
  return context;
};

// Helper to get local calendar date YYYY-MM-DD
const getTodayDateKey = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const StreakProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [streakDetails, setStreakDetails] = useState(null);
  const [showStreakModal, setShowStreakModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const userId = currentUser?.id || currentUser?.userId || currentUser?.user_id;

  // Fetch streak details from backend API
  const fetchStreakDetails = useCallback(async (openModalIfActive = false) => {
    if (!userId) return null;

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5025/api';
      const res = await fetch(`${baseUrl}/profile/streak`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!res.ok) return null;

      const json = await res.json();
      if (json && json.success && json.data) {
        setStreakDetails(json.data);

        // Check if modal should open: ONLY ONCE PER CALENDAR DAY
        if (openModalIfActive && json.data.streak > 0) {
          const todayKey = getTodayDateKey();
          const storageKey = `ks_streak_modal_seen_${userId}`;
          let alreadySeenToday = false;
          try {
            alreadySeenToday = localStorage.getItem(storageKey) === todayKey;
          } catch (e) {
            alreadySeenToday = false;
          }

          if (!alreadySeenToday) {
            setShowStreakModal(true);
            try {
              localStorage.setItem(storageKey, todayKey);
            } catch (e) {
              // Ignore localStorage quota errors
            }
          }
        }

        return json.data;
      }
    } catch (err) {
      console.error('Error fetching streak details:', err);
    } finally {
      setLoading(false);
    }
    return null;
  }, [userId]);

  // Initial load when user changes or logs in
  useEffect(() => {
    if (!userId) {
      setStreakDetails(null);
      setShowStreakModal(false);
      return;
    }

    // Check if user already has streak_details on currentUser
    if (currentUser?.streak_details) {
      setStreakDetails(currentUser.streak_details);
    } else {
      fetchStreakDetails(false);
    }
  }, [userId, currentUser, fetchStreakDetails]);

  // Trigger streak check on custom events (tactics completed, puzzle solved, game finished, etc.)
  useEffect(() => {
    const handleActivityCompleted = () => {
      fetchStreakDetails(true);
    };

    window.addEventListener('ks:activity_completed', handleActivityCompleted);
    window.addEventListener('streak:refresh', handleActivityCompleted);

    return () => {
      window.removeEventListener('ks:activity_completed', handleActivityCompleted);
      window.removeEventListener('streak:refresh', handleActivityCompleted);
    };
  }, [fetchStreakDetails]);

  const closeStreakModal = useCallback(() => {
    setShowStreakModal(false);
    if (userId) {
      const todayKey = getTodayDateKey();
      try {
        localStorage.setItem(`ks_streak_modal_seen_${userId}`, todayKey);
      } catch (e) {
        // Ignore localStorage quota errors
      }
    }
  }, [userId]);

  const triggerStreakModal = useCallback(() => {
    setShowStreakModal(true);
  }, []);

  return (
    <StreakContext.Provider
      value={{
        streakDetails,
        showStreakModal,
        loading,
        fetchStreakDetails,
        closeStreakModal,
        triggerStreakModal
      }}
    >
      {children}
    </StreakContext.Provider>
  );
};

export default StreakContext;
